import {PdsWidget} from './pds-widget';
import {PopoverAccessibilityControllerIf} from './interfaces/popover-accessibility-controller-if';
import {TippyInstance} from './interfaces/tippy-instance';
import {PopoverAccessibilityControllerFactory} from './popover-accessibility-controller-factory';
import {PoppableElementFactory} from './poppable-element-factory';
import {PopoverEngine} from './interfaces/popover-engine';
import {pdsI18n} from './pds-i18n';
import {PopperPlacement} from './popper-configuration';

/**
 * Contains all of the setup for dealing with a widget with one popover in it.
 * If more than one popover is necessary, we should build a class that deals with arrays of variables instead of single
 * instances, and this could potentially be refactored to extend the multiple version.
 */
export abstract class SinglePopoverWidget extends PdsWidget {
  protected popoverAccessibilityController: PopoverAccessibilityControllerIf;
  protected popoverTrigger: HTMLElement;
  protected popoverContentElement: HTMLElement;
  popoverInstance: TippyInstance | undefined;
  domEventListenerAdded = false;

  isPopoverShowing = false; //This gets initialized in the attached function

  attached(): void {
    super.attached();
    this.isPopoverShowing = false;
    this.createPopover();
  }

  /**
   * Gets the popover stuff all set up but may or may not call the function to actually put the popover on the screen.
   */
  createPopover(): void {
    this.log('createPopover() called');
    if (this.popoverInstance != null) {
      //If the popoverInstance has already been created, then we don't need to do it again.
      return;
    }

    if (this.isRequiredDataDefined() &&
      //Tippy must have the popover template rendered to the dom before it can work
      //because it takes the popover piece and moves it to the end of the page for rendering later
      this.querySelector(this.getPopoverSelector()) != null) {
      this.log('the data is in.  Initialize the popover');
      this.popoverAccessibilityController = PopoverAccessibilityControllerFactory.getPopoverAccessibilityControllerInstance();
      this.popoverTrigger = <HTMLElement>this.querySelector(this.getPopoverTriggerSelector());
      this.popoverContentElement = <HTMLElement>this.querySelector(this.getPopoverSelector());
      this.popoverAccessibilityController.setPanelTrigger(this.popoverTrigger);
      this.popoverAccessibilityController.setHidePanelCallback(() => { this.hidePopover(); });
      this.popoverAccessibilityController.setPanelContainer(this.popoverContentElement);
      this.popoverAccessibilityController.addRequiredAccessibilityLabelsToChildTabbableElements();

      this.createPopoverPanel();
    } else if (!this.domEventListenerAdded) {
      this.log('the data is not in.  Wait for the data to come in before initializing the popover.');
      //this gets the main template and listens to it to complete which apparently includes it's children
      //That way we ensure that the elements needed have already been added to the DOM.
      let watchElement = this.querySelector(this.getListenerSelector());
      if (watchElement == null) {
        console.error(this.getListenerSelector() + ' does not exist');
        return;
      }
      watchElement.addEventListener('dom-change', (event): void => {
        this.log('dom-change event occurred');
        this.createPopover();
      });
      this.domEventListenerAdded = true;
    }
  }

  /**
   * Actually puts the popover panel on the screen using tippy.
   */
  createPopoverPanel(): void {
    this.log('createPopoverPanel() called');
    let popoverConfig = PoppableElementFactory.generatePopoverConfig(
      false,
      this.getDefaultAlignment(), //'bottom-end',
      7000,
      'popover ' + this.is,
      this.popoverContentElement,
      (instance: PopoverEngine) => { this.onPopoverHidden(instance); },
      (instance: PopoverEngine) => { this.onPopoverShown(instance); });
    this.popoverInstance = PoppableElementFactory.generatePoppableElement(this.popoverTrigger, popoverConfig, this.is);
  }

  /**
   * This will hide the popover tippy instance.
   */
  hidePopover(): void {
    this.log('hidePopover() called');
    if (this.popoverInstance != null) {
      this.popoverInstance.hide();
    }
  }

  /**
   * Gets the selector to watch the DOM for before the popoverSelector() can be found.
   * Defaults to the popoverSelector, but can be overridden for widgets that have dom-if around the popover selector.
   * If you override this, you can give the selector for the content element of the widget.
   * @returns {string} - Selector for the element in your widget to watch.
   */
  getListenerSelector(): string {
    return this.getPopoverSelector();
  }

  /**
   * This callback is called when the popover gets closed.
   * @param popoverEngineInstance
   */
  onPopoverHidden(popoverEngineInstance: PopoverEngine): void {
    this.log('onPopoverHidden called');
    this.isPopoverShowing = false;
    this.popoverAccessibilityController.deregisterEventListenerOnPanel('keydown');
  }

  /**
   * This callback is called when the popover gets opened up for the user to see.
   * @param popoverEngineInstance
   */
  onPopoverShown(popoverEngineInstance: PopoverEngine): void {
    this.log('onPopoverShown called');
    this.isPopoverShowing = true;
    this.popoverContentElement = popoverEngineInstance.popper;
    this.popoverAccessibilityController.setPanelContainer(this.popoverContentElement);
    this.popoverContentElement.setAttribute('aria-label', pdsI18n.getText('pds|popover|Backwards From First Element'));
    this.popoverAccessibilityController.registerEventListenerOnPanel('keydown');
  }

  /**
   * When this method returns true, it informs the single-popover-widget that the template could be rendered by Polymer.
   * When it returns false, it informs the single-popover-widget not to create the popover panel yet because the template
   * will not have been rendered.
   */
  abstract isRequiredDataDefined(): boolean;

  /**
   * Get the html selector for the container that will be moved out of the DOM and into the tippy container.
   */
  abstract getPopoverSelector(): string;

  /**
   * Gets the selector of the button or anchor that will open the popover.
   */
  abstract getPopoverTriggerSelector(): string;

  abstract log(message?: any, ...optionalParams: any[]): void;

  /**
   * If you don't know what to return for this, return 'bottom-end'
   */
  abstract getDefaultAlignment(): PopperPlacement;
}
